import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ButtonsModule, ChipModule } from '@progress/kendo-angular-buttons';
import { DateInputsModule } from '@progress/kendo-angular-dateinputs';
import { DialogsModule } from '@progress/kendo-angular-dialog';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import {
  ExcelModule,
  FilterService,
  GridModule,
} from '@progress/kendo-angular-grid';
import { IconsModule } from '@progress/kendo-angular-icons';
import { BadgeModule, LoaderModule } from '@progress/kendo-angular-indicators';
import {
  FormFieldModule,
  InputsModule,
  MaskedTextBoxModule,
  NumericTextBoxModule,
  TextBoxModule,
} from '@progress/kendo-angular-inputs';
import { LabelModule } from '@progress/kendo-angular-label';
import {
  LayoutModule,
  PanelBarModule,
  TileLayoutModule,
} from '@progress/kendo-angular-layout';
import { ListViewModule } from '@progress/kendo-angular-listview';
import { MenuModule } from '@progress/kendo-angular-menu';
import { NotificationModule } from '@progress/kendo-angular-notification';
import { PopupModule } from '@progress/kendo-angular-popup';
import { TooltipsModule } from '@progress/kendo-angular-tooltip';
import { TypographyModule } from '@progress/kendo-angular-typography';
import { UploadsModule } from '@progress/kendo-angular-upload';
import { CoreModule } from '../core/core.module';
import { DocumentsModule } from './components/documents/documents.module';
import { AlertsComponent } from './components/alerts/alerts.component';
import { AuthComponent } from './components/auth/auth.component';
import { CancelMeetingComponent } from './components/cancel-meeting/cancel-meeting.component';
import { CaptureFirstAttendDateDialogComponent } from './components/capture-first-attend-date-dialog/capture-first-attend-date-dialog.component';
import { CaseClosingComponent } from './components/case-closing/case-closing.component';
import { CloseRequestForReevaluationComponent } from './components/close-request-for-reevaluation/close-request-for-reevaluation.component';
import { CustomErrorComponent } from './components/custom-error/custom-error.component';
import { DashboardComponent } from './components/dashboard/dashboard.component';
import { ActionCellComponent } from './components/grid/action-cell/action-cell.component';
import { ComplianceStatusCellComponent } from './components/grid/compliance-status-cell/compliance-status-cell.component';
import { DroplistFilterComponent } from './components/grid/droplist-filter/droplist-filter.component';
import { GridComponent } from './components/grid/grid.component';
import { ContributorDailogboxComponent } from './components/iep-contributor-dalog-box/contributor-dailogbox/contributor-dailogbox.component';
import { InfoPanelComponent } from './components/info-panel/info-panel.component';
import { NotFoundComponent } from './components/not-found/not-found.component';
import { PlacementFiltersComponent } from './components/placement-filters/placement-filters.component';
import { PlacementHistoryComponent } from './components/placement-history/placement-history.component';
import { ReasonForDelayComponent } from './components/reason-for-delay/reason-for-delay.component';
import { RequestExtensionComponent } from './components/request-extension/request-extension.component';
import { ReviewOrRequestForExtensionDialogComponent } from './components/review-or-request-for-extension-dialog/review-or-request-for-extension-dialog.component';
import { StudentProgramsComponent } from './components/student-programs/student-programs.component';
import { StudentRelatedEventsComponent } from './components/student-related-events/student-related-events.component';
import { SystemUpdatesComponent } from './components/system-updates/system-updates.component';
import { FilterEiDocumentTypePipe } from './pipes/filter-ei-document-type.pipe';
import { SortByEiDocumentTypePipe } from './pipes/sort-by-ei-document-type.pipe';
import { ContributorService } from './services/contributor-service';
import { GridService } from './services/grid.service';

import { ClipboardModule } from '@angular/cdk/clipboard';
import { FileRestrictionsDirective } from '../core/directives/file-restrictions.directive';
import { NoSpecialCharsDirective } from '../core/directives/no-special-chars.directive';
import { TrimInputDirective } from '../core/directives/trim-input.directive';
import { CharacterCounterPipe } from '../core/pipes/character-counter.pipe';
import { FormatDate } from '../core/pipes/format-date.pipe';
import { StripTagPipe } from '../core/pipes/strip-tags.pipe';
import { AssessmentScheduleMeetingComponent } from './components/assessment-schedule-meeting/assessment-schedule-meeting.component';
import { EditStudentProfileComponent } from './components/edit-student-profile/edit-student-profile.component';
import { EventsLogComponent } from './components/events-log/events-log.component';
import { MailToComponent } from './components/mail-to/mail-to.component';
import { NotAuthorizedComponent } from './components/not-authorized/not-authorized.component';
import { ReassignDailogComponent } from './components/reassign-dailog/reassign-dailog.component';
import { RequestNewDocumentComponent } from './components/request-new-document/request-new-document.component';
import { RequestReuploadComponent } from './components/request-reupload/request-reupload.component';
import { StaffSearchModule } from './components/staff-search/staff-search.module';
import { FilterByPipe } from '../core/pipes/filter-by.pipe';
import { SendBackIhmsComponent } from './components/send-back-ihms/send-back-ihms.component';
import { CompleteActionItemComponent } from './components/complete-action-item/complete-action-item.component';
import { IhmsDetailActionItemsGridComponent } from './components/ihms-detail-action-items-grid/ihms-detail-action-items-grid.component';
import { AssignActionItemDialogComponent } from './components/assign-action-item-dialog/assign-action-item-dialog.component';
import { ContextMenuModule } from '@progress/kendo-angular-menu';
import { CustomActionsModule } from './components/custom-actions/custom-actions.module';

@NgModule({
  declarations: [
    NotFoundComponent,
    CustomErrorComponent,
    DashboardComponent,
    GridComponent,
    ActionCellComponent,
    DroplistFilterComponent,
    AlertsComponent,
    SystemUpdatesComponent,
    ComplianceStatusCellComponent,
    ContributorDailogboxComponent,
    ReviewOrRequestForExtensionDialogComponent,
    RequestExtensionComponent,
    CancelMeetingComponent,
    InfoPanelComponent,
    CloseRequestForReevaluationComponent,
    SortByEiDocumentTypePipe,
    FilterEiDocumentTypePipe,
    PlacementFiltersComponent,
    StudentRelatedEventsComponent,
    CaseClosingComponent,
    AuthComponent,
    StudentProgramsComponent,
    ReasonForDelayComponent,
    CaptureFirstAttendDateDialogComponent,
    PlacementHistoryComponent,
    ReassignDailogComponent,
    RequestReuploadComponent,
    RequestNewDocumentComponent,
    EventsLogComponent,
    EditStudentProfileComponent,
    StripTagPipe,
    AssessmentScheduleMeetingComponent,
    FormatDate,
    NoSpecialCharsDirective,
    TrimInputDirective,
    FileRestrictionsDirective,
    MailToComponent,
    NotAuthorizedComponent,
    CharacterCounterPipe,
    SendBackIhmsComponent,
    CompleteActionItemComponent,
    IhmsDetailActionItemsGridComponent,
    AssignActionItemDialogComponent,
  ],
  imports: [
    CommonModule,
    TileLayoutModule,
    CoreModule,
    GridModule,
    ListViewModule,
    IconsModule,
    MenuModule,
    ButtonsModule,
    PopupModule,
    DropDownsModule,
    LabelModule,
    FormsModule,
    LayoutModule,
    PanelBarModule,
    FormFieldModule,
    NumericTextBoxModule,
    TextBoxModule,
    InputsModule,
    DateInputsModule,
    ReactiveFormsModule,
    FontAwesomeModule,
    BadgeModule,
    TooltipsModule,
    ChipModule,
    LoaderModule,
    TypographyModule,
    DialogsModule,
    ExcelModule,
    UploadsModule,
    DialogsModule,
    NotificationModule,
    MaskedTextBoxModule,
    StaffSearchModule,
    ClipboardModule,
    FilterByPipe,
    ContextMenuModule,
    CustomActionsModule,
    DocumentsModule,
  ],
  exports: [
    DashboardComponent,
    ActionCellComponent,
    ContributorDailogboxComponent,
    RequestExtensionComponent,
    InfoPanelComponent,
    SortByEiDocumentTypePipe,
    FilterEiDocumentTypePipe,
    CustomErrorComponent,
    PlacementFiltersComponent,
    StudentRelatedEventsComponent,
    CancelMeetingComponent,
    CloseRequestForReevaluationComponent,
    StudentProgramsComponent,
    CaseClosingComponent,
    ReasonForDelayComponent,
    CaptureFirstAttendDateDialogComponent,
    PlacementHistoryComponent,
    ReassignDailogComponent,
    EventsLogComponent,
    EditStudentProfileComponent,
    StripTagPipe,
    AssessmentScheduleMeetingComponent,
    FormatDate,
    NoSpecialCharsDirective,
    TrimInputDirective,
    FileRestrictionsDirective,
    MailToComponent,
    CharacterCounterPipe,
    FilterByPipe,
    IhmsDetailActionItemsGridComponent,
  ],
  providers: [GridService, FilterService, ContributorService, FormatDate],
})
export class SharedModule {}
